/* eslint-disable react/prop-types */

import React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { useMsal } from "@azure/msal-react";
import MapsSearch, {
  MapsSearchClient,
  isUnexpected,
} from "@azure-rest/maps-search";
import { debounce } from "@mui/material/utils";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PublicIcon from "@mui/icons-material/Public";
import { IAddress } from "../../data/Address";
import { MsalTokenCredential } from "./MsalTokenCredential";

interface IMapsAutocompleteProps {
  disabled?: boolean;
  onChange: (...event: any) => void;
  value: IAddress | null;
}

const searchAddress = debounce(
  async (
    client: MapsSearchClient,
    query: string,
    callback: (results?: readonly IAddress[]) => void
  ) => {
    if (!query.trim()) {
      callback([]);
      return;
    }

    const response = await client.path("/search/address/{format}", "json").get({
      queryParameters: {
        query: query,
        typeahead: true,
        lat: -25.7460186,
        lon: 28.1871204,
        limit: 5,
      },
    });

    if (isUnexpected(response)) {
      throw response.body.error;
    }

    const addresses: IAddress[] = response.body.results.map((result) => {
      const { id, address, position, viewport } = result;
      return {
        key: id,
        formatted_address: address.freeformAddress,
        street_number: address.streetNumber,
        street: address.streetName,
        postal_code: address.postalCode,
        municipality: address.municipality,
        municipality_subdivision: address.municipalitySubdivision,
        country_tertiary_subdivision: address.countryTertiarySubdivision,
        country_secondary_subdivision: address.countrySecondarySubdivision,
        country_subdivision: address.countrySubdivision,
        country: address.country,
        country_code: address.countryCode,
        position: {
          latitude: Number(position.lat.toFixed(6)),
          longitude: Number(position.lon.toFixed(6)),
        },
        viewport: {
          west: Number(viewport.topLeftPoint.lon.toFixed(6)),
          south: Number(viewport.btmRightPoint.lat.toFixed(6)),
          east: Number(viewport.btmRightPoint.lon.toFixed(6)),
          north: Number(viewport.topLeftPoint.lat.toFixed(6)),
        },
      };
    });
    callback(addresses);
  },
  400
);

export default function MapsAutocomplete({
  disabled,
  onChange,
  value,
}: IMapsAutocompleteProps) {
  const { instance, accounts } = useMsal();
  const [mapsClient, setMapsClient] = React.useState<MapsSearchClient | null>(
    null
  );
  const [search, setSearch] = React.useState<string>("");
  const [options, setOptions] = React.useState<readonly IAddress[]>([]);

  React.useEffect(() => {
    if (accounts.length > 0) {
      const account = accounts[0];

      // 1. Create your custom token credential
      const credential = new MsalTokenCredential(instance, account);

      // 2. Instantiate the Azure Maps Search client with the credential
      const mapsClient = MapsSearch(
        credential,
        "cb4c9a40-3d3b-4311-9f3e-4d06457eccd4"
      );
      setMapsClient(mapsClient);
    }
  }, [instance, accounts]);

  React.useEffect(() => {
    if (!search.trim()) {
      setOptions(value ? [value] : []);
      return;
    }

    if (mapsClient) {
      searchAddress(mapsClient, search, (results) => {
        setOptions(results || (value ? [value] : []));
      });
    }
  }, [mapsClient, value, search]);

  const getAddressDisplayText = (option: IAddress): string => {
    if (option.street) {
      return `${option.street_number ? `${option.street_number} ` : ""}${
        option.street
      }`;
    }

    const locationParts = [
      option.municipality_subdivision,
      option.municipality,
      option.country_tertiary_subdivision,
      option.country_secondary_subdivision,
      option.country_subdivision,
      option.country,
    ].filter(Boolean);

    return locationParts.length > 0 ? locationParts.slice(0, 2).join(", ") : "";
  };

  const getAddressIcon = (option: IAddress) => {
    if (option.street) return LocationOnIcon;
    if (option.municipality) return LocationCityIcon;
    return PublicIcon;
  };

  return (
    <Autocomplete
      disabled={disabled}
      getOptionLabel={(option) => option.formatted_address || "Not found"}
      getOptionKey={(option) => option.key || ""}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      value={value}
      noOptionsText="No locations"
      onChange={(_, newValue: IAddress | null) => {
        if (newValue) {
          setOptions([
            newValue,
            ...options.filter((option) => option.key !== newValue.key),
          ]);
        }
        onChange(newValue);
      }}
      onInputChange={(_, search) => {
        setSearch(search);
      }}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          label="Location"
          variant="filled"
          margin="dense"
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const IconComponent = getAddressIcon(option);
        const displayText = getAddressDisplayText(option);

        return (
          <li key={key} {...optionProps}>
            <Grid container sx={{ alignItems: "center" }}>
              <Grid sx={{ display: "flex", width: 44 }}>
                <IconComponent sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid
                sx={{
                  width: "calc(100% - 44px)",
                  wordWrap: "break-word",
                }}
              >
                <Typography
                  variant="body1"
                  noWrap
                  sx={{ color: "text.primary" }}
                >
                  {displayText}
                </Typography>
                {option.formatted_address ? (
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ color: "text.secondary" }}
                  >
                    {option.formatted_address}
                  </Typography>
                ) : null}
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
